<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку тарифов'"
                    :to="router.resolve({ name: 'HandbookDeliveryPrices' }).href"
                />
            </div>

            <div class="content-header__title">Редактирование тарифа</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isDeliveryPriceLoading" class="editable" />

        <div v-else class="content__body">
            <div class="form__content">
                <div class="form__fields">
                    <UIBrandImageableRadio
                        v-model="formBrand"
                        :error-message="errors?.brand_id?.join() ?? ''"
                    />

                    <div class="form__field-group">
                        <UISelect
                            id="model_id"
                            v-model="form.model_id"
                            :options="modelOptions"
                            label="Модель*"
                            :error-message="errors?.model_id?.join() ?? ''"
                            searchable
                        />

                        <UISelect
                            id="city_from_id"
                            v-model="form.city_from_id"
                            :options="cityFromOptions"
                            label="Город отправления*"
                            :error-message="errors?.city_from_id?.join() ?? ''"
                            searchable
                        />

                        <UISelect
                            id="city_to_id"
                            v-model="form.city_to_id"
                            :options="cityToOptions"
                            label="Город доставки*"
                            :error-message="errors?.city_to_id?.join() ?? ''"
                            searchable
                        />

                        <UIInput
                            id="price"
                            v-model="formPrice"
                            label="Стоимость доставки*"
                            only-numbers
                            :error-message="errors?.price?.join() ?? ''"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="content__footer">
            <div class="form__actions">
                <UISimpleButton
                    button-type="outlined"
                    @clicked="goToHoldingsPage"
                >
                    Выйти без сохранения
                </UISimpleButton>

                <UISimpleButton
                    :loading="saveLoading"
                    @clicked="onSave"
                >
                    Сохранить
                </UISimpleButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import { SelectOption } from '@/types/form.type.ts';
    import { City } from '@/types/city.type.ts';
    import {
        DeliveryPrice,
        DeliveryPriceUpdateForm,
        DeliveryPriceUpdateFormErrors,
        makeEmptyDeliveryPriceUpdateForm, mapDeliveryPriceUpdateFormToRequestForm,
    } from '@/types/delivery-price.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useDeliveryPriceRepository from '@/repositories/delivery-price/useDeliveryPriceRepository.ts';
    import useCityRepository from '@/repositories/city/useCityRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIBrandImageableRadio from '@/components/ui/UIBrandImageableRadio/UIBrandImageableRadio.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const router = useRouter();
    const route = useRoute();

    const { showSuccess } = useNotifications();

    const {
        getDeliveryPrice,
        updateDeliveryPrice,
    } = useDeliveryPriceRepository();
    const { getAllCities } = useCityRepository();
    const { getAllActiveModels } = useModelRepository();
    const { isLoading: isDeliveryPriceLoading, setLoading: setDeliveryPriceLoading } = useLoading();

    const form = ref<DeliveryPriceUpdateForm>(makeEmptyDeliveryPriceUpdateForm());
    const cityFromOptions = ref<SelectOption[]>([]);
    const cityToOptions = ref<SelectOption[]>([]);
    const saveLoading = ref<boolean>(false);
    const errors = ref<DeliveryPriceUpdateFormErrors | null>(null);
    const arModels = ref<Model[]>([]);

    const formBrand = computed<string | undefined>({
        get: () => {
            switch (form.value.brand_id) {
                case 1: return 'hyundai';
                case 2: return 'genesis';
                default: return '-';
            }
        },
        set: (value) => {
            if (value === 'hyundai') {
                form.value.brand_id = 1;
            }
            else if (value === 'genesis') {
                form.value.brand_id = 2;
            }
            else {
                form.value.brand_id = undefined;
            }
        },
    });

    const formPrice = computed<string>({
        get: () => form.value.price === undefined ? '' : `${form.value.price}`,
        set: (value) => {
            form.value.price = value.match(/^\d+$/) ? parseInt(value) : undefined;
        },
    });

    const modelOptions = computed<SelectOption[]>(() => {
        if (arModels.value.length === 0) {
            return [];
        }

        const options = arModels.value
            .filter((model: Model) => {
                if (form.value.brand_id === undefined) {
                    return true;
                }

                return form.value.brand_id === model.brand_id;
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));

        if (!options.find((option) => option.value === form.value.model_id)) {
            form.value.model_id = undefined;
        }

        return options;
    });

    const deliveryPriceId = route.params.delivery_price_id as unknown as number;

    function onSave(): void {
        saveLoading.value = true;
        errors.value = null;

        updateDeliveryPrice(deliveryPriceId, mapDeliveryPriceUpdateFormToRequestForm(form.value))
            .then(() => {
                showSuccess({
                    title: 'Изменения в тарфе на доставку сохранены',
                });

                goToDeliveryPricesPage();
            })
            .catch((updateFormErrors: DeliveryPriceUpdateFormErrors) => {
                errors.value = updateFormErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function goToDeliveryPricesPage(): void {
        router.push({ name: 'HandbookDeliveryPrices' });
    }

    function goToHoldingsPage(): void {
        router.push({ name: 'HandbookDeliveryPrices' });
    }

    function fetchDeliveryPrice(): void {
        setDeliveryPriceLoading(true);

        getDeliveryPrice(deliveryPriceId).then((deliveryPrice: DeliveryPrice) => {
            form.value = {
                brand_id: deliveryPrice.brand_id,
                model_id: deliveryPrice.model_id,
                city_from_id: deliveryPrice.city_from_id,
                city_to_id: deliveryPrice.city_to_id,
                price: deliveryPrice.price,
                estimated_days: deliveryPrice.estimated_days,
            };

            setDeliveryPriceLoading(false);
        });
    }

    function fetchFilterOptions(): void {
        getAllCities().then((fetchedCities: City[]) => {
            cityFromOptions.value = fetchedCities.map((city: City) => {
                return {
                    label: city.name,
                    value: city.id,
                };
            });

            cityToOptions.value = fetchedCities.map((city: City) => {
                return {
                    label: city.name,
                    value: city.id,
                };
            });
        });

        getAllActiveModels().then((fetchedModels: Model[]) => {
            arModels.value = fetchedModels;
        });
    }

    onMounted(() => {
        fetchDeliveryPrice();
        fetchFilterOptions();
    });
</script>
